import taskService from 'resources/task/task.service';
import auditService from 'resources/audit/audit.service';
import milestoneService from 'resources/milestone/milestone.service';
import { copyDocument, createDocument } from 'resources/document/workflows';
import { copyTemplateForTask } from 'resources/template/workflows/copyTemplateForTask';
import { getAssigneeId } from 'resources/task/task.helpers';
import { createTaskFilePath } from '../document/generateStoragePath';
import * as gmailApi from 'resources/gmail/gmail.api';
import * as dueHelpers from 'resources/milestone/dueDate.helpers';

const copyOrCreateTaskDocuments = async ({
  existingDocuments,
  uploadedFiles,
  newTaskId,
  dealId,
  realtorId,
  companyId,
}) => {
  if (!uploadedFiles && existingDocuments) {
    await Promise.all(existingDocuments.map((document) => copyDocument({
      copyDocumentId: document.id,
      filePath: newTaskId,
      companyId,
      dealId,
      taskId: newTaskId,
      uploaderId: realtorId,
    })));
    return;
  }

  if (uploadedFiles?.length) {
    await Promise.all(uploadedFiles.map((file) => (
      createDocument({
        file,
        bucket: 'tasks',
        filePath: createTaskFilePath({ id: newTaskId, fileName: file.name }),
        taskId: newTaskId,
        companyId,
        uploaderId: realtorId,
      })
    )));
  }
};

const calcAuditDueInDays = (audit, deal) => {
  if (!deal.due_date_calculation.exclude_weekends)
    return audit.due_in_days;

  const dueDateExclWeekends = dueHelpers.getDealAuditDueDate({
    audit,
    deal,
    calculationSettings: deal.due_date_calculation,
  });
  const dueDateExclWeekendsIsoString = dueDateExclWeekends ? dueDateExclWeekends.toISODate() : null;

  const dueInDaysInclWeekends = dueHelpers.getDealAuditDueInDays({
    dueDateIsoString: dueDateExclWeekendsIsoString,
    audit,
    deal,
  });

  return dueInDaysInclWeekends;
}

const calcTaskDueInDays = (task, deal) => {
  // skip calculation if:
  // - task.due_in_days is null
  // - exclude_weekends is false
  if (!task.due_in_days || !deal.due_date_calculation.exclude_weekends)
    return task.due_in_days;

  const dueDateExclWeekends = dueHelpers.getDealTaskDueDate({
    task,
    deal,
    calculationSettings: deal.due_date_calculation,
  });
  const dueDateExclWeekendsIsoString = dueDateExclWeekends ? dueDateExclWeekends.toISODate() : null;

  const dueInDaysInclWeekends = dueHelpers.getDealTaskDueInDays({
    dueDateIsoString: dueDateExclWeekendsIsoString,
    task,
    deal,
  });
  return dueInDaysInclWeekends;
}

const calcMilestoneDueInDays = (milestone, deal) => {
  // skip calculation if:
  // - milestone.due_in_days is null
  // - exclude_weekends is false
  if (!milestone.due_in_days || !deal.due_date_calculation.exclude_weekends)
    return milestone.due_in_days;

  const dueDateExclWeekends = dueHelpers.getDealMilestoneDueDate({
    milestone,
    deal,
    calculationSettings: deal.due_date_calculation,
  });
  const dueDateExclWeekendsIsoString = dueDateExclWeekends ? dueDateExclWeekends.toISODate() : null;

  const dueInDaysInclWeekends = dueHelpers.getDealMilestoneDueInDays({
    dueDateIsoString: dueDateExclWeekendsIsoString,
    milestone,
    deal,
  });
  return dueInDaysInclWeekends;
}

const generateMilestonesAndTasks = async ({
  deal,
  milestonesWithTasks,
}) => {
  const milestones = [];
  const defaultGmailAddress = await getDefaultGmailAddress(milestonesWithTasks, deal.company_id);

  for (const milestoneWithTasks of milestonesWithTasks) {
    if (milestoneWithTasks.isAudit) {
      const audit = milestoneWithTasks;
      await auditService.createAudit({
        companyId: deal.company_id,
        name: audit.name,
        type: audit.type,
        dealId: deal.id,
        dueInDays: calcAuditDueInDays(audit, deal),
        isAfterAction: audit.is_after_action,
        dueRelativeDate: audit.due_relative_date,
      });
    } else {
      const newMilestone = await milestoneService.createMilestone({
        companyId: deal.company_id,
        name: milestoneWithTasks.name,
        type: milestoneWithTasks.type,
        dealId: deal.id,
        editable: milestoneWithTasks.editable,
        status: milestoneWithTasks.status,
        dueInDays: calcMilestoneDueInDays(milestoneWithTasks, deal),
        isAfterAction: milestoneWithTasks.is_after_action,
        dueRelativeDate: milestoneWithTasks.due_relative_date,
      });

      for (const [index, task] of milestoneWithTasks.tasks.entries()) {
        const isTemplatedEmail = !!task.template_id;

        const newTask = await taskService.createTask({
          milestoneId: newMilestone.id,
          milestoneOrder: index,
          companyId: deal.company_id,
          content: task.content,
          assigneeId: getAssigneeId(deal, task.assignee_type),
          assigneeType: task.assignee_type,
          dealId: deal.id,
          isHiddenFromClient: task.is_hidden_from_client,
          dueInDays: calcTaskDueInDays(task, deal),
          isAfterAction: task.is_after_action,
          dueRelativeDate: task.due_relative_date,
          ...(task.description && { description: task.description }),
          ...(task.type && { type: task.type }),
          ...(task.is_optional && { isOptional: task.is_optional }),
          ...(task.is_auto && { isAuto: task.is_auto }),
          ...(isTemplatedEmail && { emailFrom: defaultGmailAddress }),
          ...(task.send_time && { sendTime: task.send_time, timeZone: task.time_zone }),
        });

        await Promise.all([
          copyOrCreateTaskDocuments({
            existingDocuments: task.tempFiles,
            uploadedFiles: task.fileData?.files,
            newTaskId: newTask.id,
            dealId: deal.id,
            realtorId: deal.realtor_id,
            companyId: deal.company_id,
          }),
          (isTemplatedEmail && (
            copyTemplateForTask({
              templateId: task.template_id,
              taskId: newTask.id,
              deal,
            })
          )),
        ]);
      }

      milestones.push(newMilestone);
    }
  }

  return milestones;
};

const getDefaultGmailAddress = async (milestonesWithTasks, companyId) => {
  if (!milestonesWithTasks) return null;

  const isEmailTaskExists = milestonesWithTasks.some((milestone) =>
    milestone && milestone.tasks && milestone.tasks.some((task) => task.template_id)
  );
  if (isEmailTaskExists) {
    const gmailIntegrations = await gmailApi.getGmailIntegrations(companyId);
    if (gmailIntegrations.length > 0) {
      const defaultGmailIntegration = gmailIntegrations.find((integration) => integration.isDefaultFrom) || gmailIntegrations[0];
      return defaultGmailIntegration.email;
    }
  }

  return null;
};

export {
  generateMilestonesAndTasks,
};
